import { A2_DISEASE_NAME_OPTIONS, ALL_PRINT_COL_VALUES } from '@/constants/consentForm';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { ConsentFormData } from '@/schemas/consentSchema';
import { parseR2CheckboxList, ConsentServiceType } from '@/utils/consentForm';

/** Derive print_cols (patient info) from non_print_cols stored in DB. */
function derivePrintCols(nonPrintCols: unknown): string[] {
  const npc = Array.isArray(nonPrintCols) ? nonPrintCols : [];
  return ALL_PRINT_COL_VALUES.filter(col => !npc.includes(col));
}

const A2_DISEASE_TOKEN_TO_ID = new Map<string, string>(
  A2_DISEASE_NAME_OPTIONS.flatMap(opt => [
    [opt.value, opt.value] as [string, string],
    [opt.label, opt.value] as [string, string],
  ])
);

function normalizeA2DiseaseTokens(value: unknown): string[] {
  let raw: unknown[] = [];

  if (Array.isArray(value)) {
    raw = value;
  } else if (typeof value === 'string' && value.trim() !== '') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        raw = Array.isArray(parsed) ? parsed : [trimmed];
      } catch {
        raw = trimmed.split(/[、,]/);
      }
    } else {
      raw = trimmed.split(/[、,]/);
    }
  } else if (value != null) {
    raw = [value];
  }

  return raw
    .map(token => String(token).trim())
    .filter(Boolean)
    .map(token => A2_DISEASE_TOKEN_TO_ID.get(token) ?? token);
}

/**
 * Map API consent detail (A2/R2) into react-hook-form partial values.
 * Shared by create (continue flow) and edit screens.
 */
export function mapDetailToFormPartial(
  raw: Record<string, unknown> | null | undefined,
  serviceType: 'a2' | 'r2'
): Partial<ConsentFormData> {
  if (!raw) return {};

  const printCols = derivePrintCols(raw.non_print_cols);

  if (serviceType.toUpperCase() === SERVICE_TYPE_R2) {
    const dn = raw.disease_name;
    const diseaseR2 =
      typeof dn === 'string' ? dn : Array.isArray(dn) ? (dn as string[]).join('、') : '';
    const { disease_name: _omitDn, ...rest } = raw;

    return {
      ...(rest as unknown as ConsentFormData),
      print_cols: printCols,
      disease_name: [],
      disease_name_r2: diseaseR2,
      walking_condition_reason_type_id: parseR2CheckboxList(raw.walking_condition_reason_type_id),
      symptom_muscle: parseR2CheckboxList(raw.symptom_muscle),
      symptom_joint: parseR2CheckboxList(raw.symptom_joint),
      symptom_joint_other: (raw.symptom_joint_other as string) ?? '',
      symptom_other: (raw.symptom_other as string) ?? '',
      is_symptom_print: Boolean(raw.is_symptom_print),
      massage_parts: parseR2CheckboxList(raw.massage),
      orthomorphia_parts: parseR2CheckboxList(raw.orthomorphia),
      hot_treatment_type_id:
        raw.hot_treatment_type_id === null || raw.hot_treatment_type_id === undefined
          ? null
          : Number(raw.hot_treatment_type_id),
    };
  }

  const dn = raw.disease_name;
  const diseaseArr = normalizeA2DiseaseTokens(dn);

  return {
    ...(raw as unknown as ConsentFormData),
    print_cols: printCols,
    disease_name: diseaseArr,
    walking_condition_reason_type_id: parseR2CheckboxList(raw.walking_condition_reason_type_id),
  };
}

/** Default empty form state used by create and as base for edit reset. */
export function getDefaultConsentFormValues(_serviceType?: ConsentServiceType): Partial<ConsentFormData> {
  return {
    print_cols: ALL_PRINT_COL_VALUES,
    non_print_cols: [],
    is_continuation: false,
    is_special: false,
    serial_number: undefined,

    injury_name: '',
    same_content: false,
    incident_cause: '',
    incident_date_type_id: 1,
    incident_date: null,
    incident_type_id: 1,
    examine_date: '',
    first_treatment_date: '',
    disease_name: [],
    disease_name_r2: '',
    disease_name_other: '',

    walking_condition_id: 1,
    is_not_transport_cost: false,
    walking_condition_reason_type_id: [],
    walking_condition_reason_other: '',
    walking_condition_reason_level: '',

    hospital_id: undefined,
    hospital_prefecture_id: null,
    hospital_name: '',
    doctor_id: undefined,
    doctor_name: '',
    hospital_postal_code1: '',
    hospital_postal_code2: '',
    hospital_address: '',
    hospital_telephone: '',

    consent_date: '',
    treatment_term_from: '',
    treatment_term_to: '',
    is_treatment_complete: false,

    is_ahaki_both_treatment: false,
    treatment_content: [],
    remarks: '',

    symptom_muscle: [],
    symptom_joint: [],
    symptom_joint_other: '',
    is_symptom_print: true,
    massage_parts: [],
    orthomorphia_parts: [],
    hot_treatment_type_id: null,
  };
}
